/**
 * Accueil de l'espace apprenant (04/11/2026) : couverture, position de l'image, message du formateur.
 *
 * Deux niveaux, comme les pages d'un espace Notion :
 *  - les réglages PAR DÉFAUT du formateur (son profil) ;
 *  - une SURCHARGE facultative par dossier : chaque champ laissé vide hérite du défaut.
 *
 * Fonctions pures : la saisie est contrôlée ici, la résolution (« quelle couverture voit l'apprenant ? ») aussi.
 * Le serveur stocke `couverture` sous forme de texte : `galerie:<id>`, `image`, ou `""` (hériter / défaut).
 */
import { COUVERTURE_PAR_DEFAUT, couvertureGalerie } from "./couvertures";

export const MESSAGE_ACCUEIL_MAX = 600;
export const POSITION_PAR_DEFAUT = 50;

export type CouvertureLue = { type: "galerie"; id: string } | { type: "image" };

/** `galerie:aurore` → { galerie, aurore } ; `image` → { image } ; vide ou inconnu → null (hériter). */
export function lireCouverture(texte: string | null | undefined): CouvertureLue | null {
  if (!texte) return null;
  if (texte === "image") return { type: "image" };
  const m = /^galerie:([a-z0-9-]{1,40})$/.exec(texte);
  return m && couvertureGalerie(m[1]!) ? { type: "galerie", id: m[1]! } : null;
}

/** Message saisi : texte brut, caractères de contrôle retirés, au plus deux sauts de ligne consécutifs. */
export function normaliserMessage(texte: string): string {
  return (
    texte
      .replace(/\r\n?/g, "\n")
      // eslint-disable-next-line no-control-regex
      .replace(/[\u0000-\u0008\u000B-\u001F\u007F]/g, "")
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim()
  );
}

export interface SaisieAccueil {
  /** `galerie:<id>` ou `image` ; `""` = hériter (surcharge) ou couverture par défaut (profil). */
  couverture: string;
  position: number;
  /** `null` = hériter du message par défaut (surcharge) ; `""` n'est pas admis au niveau du profil (on hérite du texte de la plateforme). */
  message: string | null;
}

export type ResultatSaisie =
  { ok: true; valeur: SaisieAccueil } | { ok: false; erreurs: Record<string, string> };

/**
 * Contrôle une saisie venue de l'interface. `aUneImage` : une image est-elle déjà déposée pour ce niveau ? (on ne
 * choisit pas « image » sans image).
 */
export function validerSaisieAccueil(entree: unknown, aUneImage: boolean): ResultatSaisie {
  const e = (entree && typeof entree === "object" ? entree : {}) as Record<string, unknown>;
  const erreurs: Record<string, string> = {};

  const couverture = typeof e.couverture === "string" ? e.couverture : "";
  if (couverture !== "" && lireCouverture(couverture) === null)
    erreurs.couverture = "Couverture inconnue : choisissez-en une dans la galerie.";
  if (couverture === "image" && !aUneImage) erreurs.couverture = "Déposez d'abord une image de couverture.";

  let position = POSITION_PAR_DEFAUT;
  if (e.position !== undefined && e.position !== null) {
    const n = Number(e.position);
    if (!Number.isFinite(n)) erreurs.position = "Position invalide.";
    else position = Math.min(100, Math.max(0, Math.round(n)));
  }

  let message: string | null = null;
  if (typeof e.message === "string") {
    const m = normaliserMessage(e.message);
    if (m.length > MESSAGE_ACCUEIL_MAX)
      erreurs.message = `Message trop long : ${MESSAGE_ACCUEIL_MAX} caractères au plus (${m.length} saisis).`;
    message = m === "" ? null : m;
  } else if (e.message !== undefined && e.message !== null) erreurs.message = "Message invalide.";

  return Object.keys(erreurs).length > 0
    ? { ok: false, erreurs }
    : { ok: true, valeur: { couverture, position, message } };
}

/** Texte d'accueil de la plateforme, quand le formateur n'a rien écrit. */
export function messageParDefaut(prenomFormateur: string): string {
  const signe = prenomFormateur.trim() ? `\n\n${prenomFormateur.trim()}, votre formateur` : "";
  return `Bienvenue dans votre espace de formation ! Vous y trouverez, étape par étape, les formulaires à remplir, les documents à signer et vos supports de cours.${signe}`;
}

export interface NiveauAccueil {
  couverture: string;
  position: number;
  message: string | null;
  /** Adresse de lecture de l'image déposée à ce niveau, ou null. */
  image: string | null;
}

export type CouvertureResolue =
  | { type: "galerie"; id: string; fond: string; position: number }
  | { type: "image"; url: string; position: number };

export interface AccueilResolu {
  couverture: CouvertureResolue;
  message: string;
  /** Ce qui vient du dossier (et non du profil du formateur). */
  surcharge: { couverture: boolean; message: boolean };
}

function couvertureDe(n: NiveauAccueil | null): CouvertureResolue | null {
  const c = lireCouverture(n?.couverture);
  if (!n || !c) return null;
  if (c.type === "image") return n.image ? { type: "image", url: n.image, position: n.position } : null;
  return { type: "galerie", id: c.id, fond: couvertureGalerie(c.id)!.fond, position: n.position };
}

/** Ce que voit l'apprenant : la surcharge du dossier, sinon le profil du formateur, sinon la plateforme. */
export function resoudreAccueil(
  defaut: NiveauAccueil | null,
  surcharge: NiveauAccueil | null,
  prenomFormateur: string,
): AccueilResolu {
  const cDossier = couvertureDe(surcharge);
  const couverture = cDossier ??
    couvertureDe(defaut) ?? {
      type: "galerie" as const,
      id: COUVERTURE_PAR_DEFAUT,
      fond: couvertureGalerie(COUVERTURE_PAR_DEFAUT)!.fond,
      position: POSITION_PAR_DEFAUT,
    };
  const mDossier = surcharge?.message ?? null;
  return {
    couverture,
    message: mDossier ?? defaut?.message ?? messageParDefaut(prenomFormateur),
    surcharge: { couverture: cDossier !== null, message: mDossier !== null },
  };
}
